import React, { Fragment, useState, useEffect } from 'react';
import moment from 'moment';
import NumericalLabel from 'react-pretty-numbers';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tabs, Tab, TableRow, TableCell } from '@material-ui/core';
import { TableListComp } from '../../../shared_elements';
import { getCurrencyFormat, permissionCheckFn } from '../../../utils';
import { trackActivity } from '../../../utils/mixpanel';

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/no-data-to-display')(Highcharts);

const toSeriesFromRows = (rows, valueKey) => {
  if (!rows || !rows.length) return [];
  return rows.map((row) => [moment(row.date).valueOf(), Number(row[valueKey])]);
};

const toSeriesFromGraph = (years, values, valueAsOf) => {
  if (!years || !values || !years.length) return [];
  const asOf = valueAsOf ? moment(valueAsOf) : null;
  const month = asOf ? asOf.month() : 0;
  const day = asOf ? asOf.date() : 1;
  return years.map((year, index) => [
    Date.UTC(year, month, day),
    values[index] != null ? Number(values[index]) : null,
  ]);
};

const EngineFutureProjectionsModal = ({ open, onClose, logResult, onRevealFullLife }) => {
  const [tab, setTab] = useState('values');
  const inflation = logResult.escalation_percentage != null ? logResult.escalation_percentage : 2;
  const fbvRows = logResult.future_base_value_rows
    || (logResult.engine_valuation && logResult.engine_valuation.projections
      && logResult.engine_valuation.projections.values
      && logResult.engine_valuation.projections.values.future_base_value)
    || [];
  const blrRows = logResult.future_blr_rows
    || (logResult.engine_valuation && logResult.engine_valuation.projections
      && logResult.engine_valuation.projections.values
      && logResult.engine_valuation.projections.values.future_blr)
    || [];
  const graph = logResult.projection_graph
    || (logResult.engine_valuation && logResult.engine_valuation.projections
      && logResult.engine_valuation.projections.graph)
    || null;
  const showBlr = logResult.is_future_blr
    || (!('is_future_blr' in logResult) && (blrRows.length || (logResult.future_blr_value && Object.keys(logResult.future_blr_value).length)));
  const valueAsOf = logResult.base_value_date || logResult.value_as_of;

  const getPlots = (projection) => {
    if (!projection) return [];
    return Object.keys(projection).map((yearKey) => {
      const month = yearKey.slice(4, 6) || '01';
      return [new Date(`${month}/01/${yearKey.slice(0, 4)}`).getTime(), projection[yearKey]];
    });
  };

  const buildFbvSeries = () => {
    if (graph && graph.future_base_value && graph.years) {
      const fbv = graph.future_base_value;
      return [
        { name: 'HL BV @ 0%', data: toSeriesFromGraph(graph.years, fbv.half_life_0, valueAsOf), color: '#7fb2ff' },
        { name: `HL BV @ ${inflation}%`, data: toSeriesFromGraph(graph.years, fbv.half_life_inflation, valueAsOf), color: '#2b2b3a' },
        { name: 'FL BV @ 0%', data: toSeriesFromGraph(graph.years, fbv.full_life_0, valueAsOf), color: '#86d36e' },
        { name: `FL BV @ ${inflation}%`, data: toSeriesFromGraph(graph.years, fbv.full_life_inflation, valueAsOf), color: '#eea24a' },
      ].filter(s => s.data && s.data.length);
    }
    if (fbvRows.length) {
      return [
        { name: 'HL BV @ 0%', data: toSeriesFromRows(fbvRows, 'half_life_0'), color: '#7fb2ff' },
        { name: `HL BV @ ${inflation}%`, data: toSeriesFromRows(fbvRows, 'half_life_inflation'), color: '#2b2b3a' },
        { name: 'FL BV @ 0%', data: toSeriesFromRows(fbvRows, 'full_life_0'), color: '#86d36e' },
        { name: `FL BV @ ${inflation}%`, data: toSeriesFromRows(fbvRows, 'full_life_inflation'), color: '#eea24a' },
      ];
    }
    const series = [];
    if (logResult.future_value) {
      series.push({ name: 'HL BV @ 0%', data: getPlots(logResult.future_value), color: '#7fb2ff' });
    }
    if (logResult.inflated_future_value) {
      series.push({ name: `HL BV @ ${inflation}%`, data: getPlots(logResult.inflated_future_value), color: '#2b2b3a' });
    }
    if (logResult.full_life_future_values) {
      series.push({ name: 'FL BV @ 0%', data: getPlots(logResult.full_life_future_values), color: '#86d36e' });
    }
    if (logResult.full_life_inflated_future_value) {
      series.push({ name: `FL BV @ ${inflation}%`, data: getPlots(logResult.full_life_inflated_future_value), color: '#eea24a' });
    }
    return series;
  };

  const buildBlrSeries = () => {
    if (graph && graph.future_blr && graph.years) {
      return [
        { name: 'BLR @ 0%', data: toSeriesFromGraph(graph.years, graph.future_blr.blr_0, valueAsOf), color: '#7fb2ff' },
        { name: `BLR @ ${inflation}%`, data: toSeriesFromGraph(graph.years, graph.future_blr.blr_inflation, valueAsOf), color: '#2b2b3a' },
      ].filter(s => s.data && s.data.length);
    }
    if (blrRows.length) {
      return [
        { name: 'BLR @ 0%', data: toSeriesFromRows(blrRows, 'blr_0'), color: '#7fb2ff' },
        { name: `BLR @ ${inflation}%`, data: toSeriesFromRows(blrRows, 'blr_inflation'), color: '#2b2b3a' },
      ];
    }
    const series = [];
    if (logResult.future_blr_value) {
      series.push({ name: 'BLR @ 0%', data: getPlots(logResult.future_blr_value), color: '#7fb2ff' });
    }
    if (logResult.inflated_future_blr_value) {
      series.push({ name: `BLR @ ${inflation}%`, data: getPlots(logResult.inflated_future_blr_value), color: '#2b2b3a' });
    }
    return series;
  };

  const chartOptions = (series, yTitle) => ({
    chart: { type: 'line', marginTop: 40 },
    title: { text: null },
    xAxis: { type: 'datetime', title: { text: 'Projection Year(s)' } },
    yAxis: { title: { text: yTitle } },
    series,
    exporting: permissionCheckFn({ secondaryKey: 'future_value', keyIndex: 'U' }) ? {
      buttons: {
        contextButton: {
          menuItems: [{
            text: 'Export as PNG',
            onclick: function () {
              this.exportChart();
              trackActivity('Report Exported', { file_type: 'PNG', report_type: 'Chart', type: 'Engine Future Projections' });
            }
          }]
        }
      }
    } : false,
    credits: { enabled: false },
  });

  const legacyFutureKeys = logResult.future_value ? Object.keys(logResult.future_value) : [];

  useEffect(() => {
    if (open && onRevealFullLife) {
      onRevealFullLife();
    }
  }, [open, onRevealFullLife]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="engine-future-projections-modal">
      <DialogTitle className="engine-modal-head">Future Projections</DialogTitle>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary" className="engine-modal-tabs">
        <Tab label="Values" value="values" />
        <Tab label="Graph" value="graph" />
      </Tabs>
      <DialogContent dividers className="engine-modal-body">
        {tab === 'values' ? (
          <Fragment>
            <h4 className="engine-modal-subhead">Future Base Value <span>(US$ m)</span></h4>
            <TableListComp
              heads={[
                { label: 'Date', sortOption: false },
                { label: 'Half Life @ 0% Inflation', sortOption: false },
                { label: `Half Life @ ${inflation}% Inflation`, sortOption: false },
                { label: 'Full Life @ 0% Inflation', sortOption: false },
                { label: `Full Life @ ${inflation}% Inflation`, sortOption: false },
              ]}
              data={fbvRows.length ? fbvRows.map((row) => (
                <TableRow key={row.date || row.label}>
                  <TableCell>{row.label || moment(row.date).format('MMM YYYY')}</TableCell>
                  <TableCell>
                    <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                      {row.half_life_0}
                    </NumericalLabel> m
                  </TableCell>
                  <TableCell>
                    <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                      {row.half_life_inflation}
                    </NumericalLabel> m
                  </TableCell>
                  <TableCell>
                    <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                      {row.full_life_0}
                    </NumericalLabel> m
                  </TableCell>
                  <TableCell>
                    <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                      {row.full_life_inflation}
                    </NumericalLabel> m
                  </TableCell>
                </TableRow>
              )) : legacyFutureKeys.map((label) => {
                const date = new Date(label.slice(0, 4), `${label.slice(4, 6) || '01'}` - 1);
                return (
                  <TableRow key={label}>
                    <TableCell>{moment(date).format('MMM YYYY')}</TableCell>
                    <TableCell>
                      <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                        {logResult.future_value[label]}
                      </NumericalLabel> m
                    </TableCell>
                    <TableCell>
                      {logResult.inflated_future_value && logResult.inflated_future_value[label] != null ? (
                        <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                          {logResult.inflated_future_value[label]}
                        </NumericalLabel>
                      ) : '—'} m
                    </TableCell>
                    <TableCell>
                      {logResult.full_life_future_values && logResult.full_life_future_values[label] != null ? (
                        <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                          {logResult.full_life_future_values[label]}
                        </NumericalLabel>
                      ) : '—'} m
                    </TableCell>
                    <TableCell>
                      {logResult.full_life_inflated_future_value && logResult.full_life_inflated_future_value[label] != null ? (
                        <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>
                          {logResult.full_life_inflated_future_value[label]}
                        </NumericalLabel>
                      ) : '—'} m
                    </TableCell>
                  </TableRow>
                );
              })}
            />

            {showBlr ? (
              <Fragment>
                <h4 className="engine-modal-subhead">Future Base Lease Rate (BLR)</h4>
                <TableListComp
                  heads={[
                    { label: 'Date', sortOption: false },
                    { label: 'BLR @ 0% Inflation', sortOption: false },
                    { label: `BLR @ ${inflation}% Inflation`, sortOption: false },
                  ]}
                  data={blrRows.length ? blrRows.map((row) => (
                    <TableRow key={row.date || row.label}>
                      <TableCell>{row.label || moment(row.date).format('MMM YYYY')}</TableCell>
                      <TableCell>{row.blr_0 != null ? Number(row.blr_0).toFixed(2) : '—'}</TableCell>
                      <TableCell>{row.blr_inflation != null ? Number(row.blr_inflation).toFixed(2) : '—'}</TableCell>
                    </TableRow>
                  )) : (logResult.future_blr_value ? Object.keys(logResult.future_blr_value) : []).map((label) => {
                    const date = new Date(label.slice(0, 4), `${label.slice(4, 6) || '01'}` - 1);
                    return (
                      <TableRow key={label}>
                        <TableCell>{moment(date).format('MMM YYYY')}</TableCell>
                        <TableCell>{logResult.future_blr_value[label] != null ? Number(logResult.future_blr_value[label]).toFixed(2) : '—'}</TableCell>
                        <TableCell>
                          {logResult.inflated_future_blr_value && logResult.inflated_future_blr_value[label] != null
                            ? Number(logResult.inflated_future_blr_value[label]).toFixed(2)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                />
                <p className="engine-mlr-note">Market Lease Rate (MLR) is a current-value figure and is not projected.</p>
              </Fragment>
            ) : null}
          </Fragment>
        ) : (
          <Fragment>
            <h4 className="engine-modal-subhead">Future Base Value</h4>
            <HighchartsReact highcharts={Highcharts} options={chartOptions(buildFbvSeries(), 'US$ m')} />
            {showBlr ? (
              <Fragment>
                <h4 className="engine-modal-subhead">Future Base Lease Rate (BLR)</h4>
                <HighchartsReact highcharts={Highcharts} options={chartOptions(buildBlrSeries(), 'Lease Rate factor')} />
              </Fragment>
            ) : null}
          </Fragment>
        )}
      </DialogContent>
      <DialogActions className="engine-modal-foot">
        <Button onClick={onClose} color="primary" variant="outlined" size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EngineFutureProjectionsModal;
